<script>
import { mapState, mapGetters } from 'vuex';
import { Plugins } from '@capacitor/core';
import initPushNotifications from '@/utils/PushNotifications';
import hideStatusBar from '@/utils/StatusBar';
import TopBar from '@/components/TopBar.vue';
import BottomBar from '@/components/BottomBar.vue';
import Snackbar from '@/components/Snackbar.vue';

const { App: CapApp, SplashScreen, UtilsPlugin } = Plugins;

const Paywall = () =>
  import(/* webpackChunkName: "paywall" */ '@/components/subscription/Paywall.vue');

export default {
  name: 'App',

  components: {
    TopBar,
    BottomBar,
    Snackbar,
    Paywall,
  },

  data: () => ({
    safeAreaTop: 0,
    safeAreaBottom: 0,
    keyboardHeight: 0,
    routeTransitionName: 'none',
  }),

  computed: {
    ...mapState('ui', ['routeName', 'routeMeta', 'paywall']),

    ...mapGetters('ui', ['routeLoaded', 'currentCategory']),

    ...mapState('device', ['isNativeApp', 'isIOSApp', 'isAndroidApp']),

    ...mapState('dailyCards', ['dailyCardsLoaded']),
  },

  watch: {
    $route: {
      handler: 'getRoute',
      immediate: true,
    },
    dailyCardsLoaded: {
      handler: 'hideSplashScreen',
    },
  },

  created() {
    const { isNativeApp, isIOSApp, isAndroidApp, $store } = this;

    if (isNativeApp) {
      hideStatusBar();

      CapApp.addListener('appUrlOpen', ({ url }) => {
        $store.dispatch('ui/handleNativeDeepLink', url);
      });
      // TODO Make sure physical back button behavior matches top bar button
      CapApp.addListener('backButton', () => {
        if (this.paywall) {
          this.$store.dispatch('ui/hidePaywall');
        } else if (this.$route.name === 'AppHome') {
          CapApp.exitApp();
        } else {
          this.$router.go(-1);
        }
      });

      window.addEventListener('keyboardWillShow', ({ keyboardHeight = 0 }) => {
        this.keyboardHeight = keyboardHeight;
      });
      window.addEventListener('keyboardWillHide', () => {
        this.keyboardHeight = 0;
      });

      initPushNotifications();
    } else {
      window.addEventListener('beforeinstallprompt', e => {
        console.log('Stashing installPrompt');
        // Prevent Chrome 67 and earlier from automatically showing the prompt
        e.preventDefault();
        // Stash the event so it can be triggered later.
        // $store.dispatch('sw/updateInstallPrompt', e);
      });

      window.addEventListener('appinstalled', () => {
        console.log('PWA installed');
        // Only relevant if clicked button from prompt
        // this.$store.commit('sw/setAcceptedInstallPrompt', true);
        // this.$store.dispatch('analytics/acceptAddToHomeScreen');
      });
    }
    if (isAndroidApp) {
      this.setAndroidSafeAreas();
    }

    if (isIOSApp) {
      this.getPreferredFontSize();
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.getSafeAreas();
      const { routeName, safeAreaBottom } = this;
      // On iOS, the variables aren't picked up right away when the app is first installed
      // So trying again, but limiting to Auth route to avoid running function twice on every app open
      if ((routeName === 'Auth' || !routeName) && safeAreaBottom === 0) {
        setTimeout(() => {
          this.getSafeAreas();
        }, 3000);
      }
    });
  },

  methods: {
    getRoute($route = {}, oldRoute = {}) {
      // console.log($route);
      const newHasBottomBar = $route.meta && $route.meta.hasBottomBar;
      const oldHasBottomBar = oldRoute.meta && oldRoute.meta.hasBottomBar;
      this.routeTransitionName = newHasBottomBar && oldHasBottomBar ? 'none' : 'flip';
      this.$store.dispatch('ui/parseRoute', $route);
      const appContainer = document.getElementById('main-app-container');
      if (appContainer) {
        appContainer.scrollTop = 0;
      }
    },

    hideSplashScreen() {
      if (this.isNativeApp) {
        SplashScreen.hide();
      }
    },

    async setAndroidSafeAreas() {
      try {
        // Don't need bottom inset because not hiding bottom navigation (which adapts automatically)
        const { safeInsetTop = 0 } = await UtilsPlugin.getSafeAreas();
        if (+safeInsetTop > 0) {
          document.documentElement.style.setProperty(
            '--android-safe-area-inset-top',
            `${safeInsetTop}px`
          );
        }
      } catch (error) {
        console.error('Error getting Android safe areas', error);
      }
    },

    getSafeAreas() {
      const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue(
        '--safe-area-inset-top'
      );
      const safeAreaBottom = getComputedStyle(document.documentElement).getPropertyValue(
        '--safe-area-inset-bottom'
      );
      this.safeAreaTop = +safeAreaTop.split('px')[0] || 0;
      this.safeAreaBottom = +safeAreaBottom.split('px')[0] || 0;
    },

    // In case people on iOS have a larger font for accessibility,
    // Get user's preferred body font
    async getPreferredFontSize() {
      try {
        const { fontSize = 16 } = await UtilsPlugin.getPreferredFontSize();
        if (+fontSize > 18) {
          document.documentElement.style.setProperty('font-size', `${fontSize}px`);
        }
      } catch (error) {
        console.error('Error getting preferred font size', error);
      }
    },
  },
};
</script>

<template>
  <div id="app" class="app" :class="$vuetify.breakpoint.name">
    <v-app
      v-if="routeLoaded"
      id="v-app"
      class="background app-container"
      :class="[currentCategory, `${$route.name.toLowerCase()}-route`]"
    >
      <TopBar v-show="routeMeta.hasTopBar" :safe-area="safeAreaTop" />

      <BottomBar
        v-show="routeMeta.hasBottomBar"
        :safe-area="safeAreaBottom"
        :route-name="routeName"
      />

      <!-- Loads child routes -->
      <v-main
        id="main-app-container"
        :style="{ maxHeight: `calc(100vh - ${keyboardHeight}px)` }"
        :class="{
          'top-bar-layout': routeMeta.hasTopBar,
          'bottom-bar-layout': routeMeta.hasBottomBar,
        }"
      >
        <transition :name="routeTransitionName">
          <router-view :key="$route.name" />
        </transition>
      </v-main>

      <Snackbar />

      <transition name="dialog">
        <Paywall v-if="paywall" />
      </transition>
    </v-app>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

#app {
  &.lg {
    margin: 0 auto;
  }
}

.app-container {
  height: 100vh;
  overflow-y: hidden;
}

.theme--light.v-application {
  background: unset;
  background-image: url('./assets/backgrounds/BlueBackground.svg?external');
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;

  &.deepening {
    background-image: url('./assets/backgrounds/PurpleBackground.svg?external');
  }

  &.lightening {
    background-image: url('./assets/backgrounds/YellowBackground.svg?external');
  }
}

#main-app-container {
  position: relative;
  height: 100vh;
  // padding-top: $top-bar-height !important;
  overflow-y: auto;
}

.flip-enter-active {
  transition: transform 0.4s ease-out;
}
.flip-leave-active {
  transition: transform 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.flip-enter,
.flip-leave-to {
  transform: rotate3d(0, 1, 0, 60deg);
}

// .dialog-leave-active {
//   opacity: 1;
//   transition-delay: 1s;
//   transition-duration: 0;
// }
// .dialog-enter,
// .dialog-leave-to {
//   opacity: 0;
// }

@media (prefers-reduced-motion: reduce) {
  .flip-enter-active,
  .flip-leave-active {
    transition: none;
  }
  .flip-leave-active {
    transition: none;
  }
  .flip-enter,
  .flip-leave-to {
    transform: none;
  }
}
</style>
